<template>
  <div v-tooltip="t('canvasUI.components.CFeedback.feedback.tooltip')">
    <CPopover v-model:show="show" content-class="pa-2" :show-arrow="false" to="body" trigger="click">
      <template #trigger>
        <slot>
          <CIcon icon="message-exclamation" size="16px" />
        </slot>
      </template>

      <div class="flex flex-col gap-4">
        <div class="font-500 font-size-5">{{ t('canvasUI.components.CFeedback.title') }}</div>

        <div class="flex gap-4 items-center cursor-pointer" @click="() => launchFeedbackIntegration('bug')">
          <CIcon class="c-feedback__icon" icon="bug" size="24px" />

          <div class="flex flex-col">
            <div class="font-size-[18px]">{{ t('canvasUI.components.CFeedback.bug.title') }}</div>
            <div class="font-size-[14px]">{{ t('canvasUI.components.CFeedback.bug.subtitle') }}</div>
          </div>
        </div>

        <div class="flex gap-4 items-center cursor-pointer" @click="() => launchFeedbackIntegration('feedback')">
          <CIcon class="c-feedback__icon" icon="lightbulb" size="24px" />

          <div class="flex flex-col">
            <div class="font-size-[18px]">{{ t('canvasUI.components.CFeedback.feedback.title') }}</div>
            <div class="font-size-[14px]">{{ t('canvasUI.components.CFeedback.feedback.subtitle') }}</div>
          </div>
        </div>
      </div>
    </CPopover>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue'
import type { feedbackIntegration as FeedbackIntegration } from '@sentry/vue'
import { useI18n } from 'vue-i18n'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import CPopover from '@lib/components/CPopover/CPopover.vue'
import tooltipDirective from '@lib/directives/tooltip'
import useThemeVars from '@lib/composables/useThemeVars'
import { CDP_EVENT_TYPE } from '@lib/constants/cdp.const'
import type { SubmitUserFeedbackPayload } from '@lib/sdk/payload.types'

let widgetForm: Awaited<ReturnType<ReturnType<typeof FeedbackIntegration>['createForm']>> | null = null
const emit = defineEmits<{
  submit: [d: SubmitUserFeedbackPayload]
}>()
const props = defineProps<{
  feedbackIntegration: typeof FeedbackIntegration
  track: (eventName: string, payload: any) => void
}>()
const { t } = useI18n()
const vTooltip = tooltipDirective
const themeVars = useThemeVars()
const show = ref(false)
const activeTab: Ref<SubmitUserFeedbackPayload['type'] | null> = ref(null)
const getSentry = () => document.querySelector('#sentry-feedback')
const getForm = () => getSentry()?.shadowRoot?.querySelector('form')

function resetState() {
  activeTab.value = null
}

function captureFeedbackOnTracker() {
  const form = getForm()
  const message: HTMLTextAreaElement | undefined = form?.querySelector('textarea[name="message"]') ?? undefined

  if (!message || !form) {
    throw new Error('Form or message is not found')
  }

  const formData = new FormData(form)
  const data: Record<string, any> = {}

  for (const [key, value] of formData) {
    data[key] = value
  }

  data.message = message.value

  const eventName = activeTab.value === 'bug' ? CDP_EVENT_TYPE.userBugReported : CDP_EVENT_TYPE.userFeedbackCaptured

  props.track(eventName, data)
  if (activeTab.value) emit('submit', { type: activeTab.value, data })
  resetState()
}

function hidePopup() {
  widgetForm?.removeFromDom()
  show.value = false
}

function handleInput(event: Event) {
  localStorage.setItem('feedbackMessage', (event.target as HTMLTextAreaElement).value)
}

function handleCancel() {
  localStorage.removeItem('feedbackMessage')
}

let parentElement: HTMLElement | undefined | null = null
let parentClickHandler: ((event: Event) => void) | null = null

async function launchFeedbackIntegration(type: SubmitUserFeedbackPayload['type']) {
  activeTab.value = type
  show.value = false

  let formTitle = ''
  let messagePlaceholder = ''
  const prefix = type === 'bug' ? '[BUG] ' : '[IDEA] '

  if (type === 'bug') {
    formTitle = t('canvasUI.components.CFeedback.bug.title')
    messagePlaceholder = t('canvasUI.components.CFeedback.bug.messagePlaceholder')
  } else {
    formTitle = t('canvasUI.components.CFeedback.feedback.title')
    messagePlaceholder = t('canvasUI.components.CFeedback.feedback.messagePlaceholder')
  }

  const feedback = props.feedbackIntegration({
    enableScreenshot: false,
    formTitle,
    messagePlaceholder,
    showBranding: false,
    showEmail: false,
    showName: false,
    autoInject: false,
    messageLabel: false,
    isRequiredLabel: false,
    useSentryUser: {
      email: 'email',
      name: 'fullName',
    },
    submitButtonLabel: t('canvasUI.components.CFeedback.submit'),
    cancelButtonLabel: t('canvasUI.components.CFeedback.cancel'),
    successMessageText: t('canvasUI.components.CFeedback.successMessage'),
    colorScheme: 'light',
    themeLight: {
      accentBackground: themeVars.value.primary,
      accentForeground: themeVars.value.base,
      inputBorder: `1px solid ${themeVars.value.primary}`,
      successColor: themeVars.value.success,
      errorColor: themeVars.value.error,
    },
    onFormOpen: () => {
      getForm()?.addEventListener('submit', captureFeedbackOnTracker)
      const savedMessage = localStorage.getItem('feedbackMessage') || ''
      const prefixedMessage = prefix + savedMessage.replace(/^\[BUG\] |\[IDEA\] /, '')
      const m = getForm()?.querySelector('#message') as HTMLTextAreaElement | null
      if (m) m.value = prefixedMessage
      const textarea = getForm()?.querySelector('textarea[name="message"]') as HTMLTextAreaElement | null

      if (textarea) {
        textarea.addEventListener('input', handleInput)
      }

      // Add event listener to the parent element
      parentElement = getForm()?.parentElement
      parentClickHandler = (event: Event) => {
        const target = event.target as HTMLElement
        if (
          target.matches('button.btn--default[type="button"]') &&
          target.textContent?.trim() === t('canvasUI.components.CFeedback.cancel')
        ) {
          handleCancel()
        }
      }

      if (parentElement) {
        parentElement.addEventListener('click', parentClickHandler)
      } else {
        console.warn('Parent element not found')
      }
    },
    onFormClose: () => {
      const textarea = getForm()?.querySelector('textarea[name="message"]') as HTMLTextAreaElement | null
      if (textarea) textarea.removeEventListener('input', handleInput)
      getForm()?.removeEventListener('submit', captureFeedbackOnTracker)
      setTimeout(() => {
        if (parentElement && parentClickHandler) parentElement.removeEventListener('click', parentClickHandler)
      })

      resetState()
      hidePopup()
    },
    onSubmitSuccess: () => {
      localStorage.removeItem('feedbackMessage')
    },
  })

  if (!feedback) {
    console.warn('Sentry is not registered.')
    return null
  }

  getSentry()?.remove()

  widgetForm = await feedback.createForm()
  widgetForm.appendToDom()
  widgetForm.open()
  setTimeout(() => {
    const m = getForm()?.querySelector('#message') as HTMLTextAreaElement | null
    m?.focus()
  }, 100)
}

function handleKeyDown(event: KeyboardEvent) {
  if ((event.target as HTMLElement)?.id === 'sentry-feedback') {
    event.stopPropagation()
  }
}

function handleFocus(event: FocusEvent) {
  if ((event.target as HTMLElement)?.id === 'sentry-feedback') {
    event.stopPropagation()
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown, true)
  window.addEventListener('focus', handleFocus, true)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyDown, true)
  window.removeEventListener('focus', handleFocus, true)
})

defineExpose({ hidePopup })
</script>

<style lang="scss">
.c-feedback {
  &__title,
  &__card-title {
    letter-spacing: 0.25px;
  }

  &__icon {
    color: v-bind('themeVars.primary4');
  }
}

#sentry-feedback#sentry-feedback {
  --font-family: 'Roboto', sans-serif;
  --dialog-border-radius: 4px;
  --button-primary-border-radius: 4px;
  --button-primary-border: none;
  --button-border-radius: 4px;
  --button-border: none;
  --input-border-radius: 4px;
  --dialog-border: none;
}
</style>
